import React, { useEffect, useMemo, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { colors } from "../../constants/site-content";
export const IOS_LATEST_VERSION = "1.11";


type VersionInfoResponse = {
  ios?: { latestVersion?: string | null; downloadUrl?: string | null };
  android?: { latestVersion?: string | null; downloadUrl?: string | null };
  forceUpdate?: boolean;
};

const CROQEE_SERVER_URL = process.env.REACT_APP_CROQEE_SERVER_URL || 'https://croqeeserver.online';

function parseVersion(version: string): number[] {
  return (version || "")
    .split(".")
    .map((part) => parseInt(part.replace(/[^0-9]/g, ""), 10))
    .map((n) => (Number.isFinite(n) ? n : 0));
}

function isStoreNewer(storeVersion: string, currentVersion: string): boolean {
  const a = parseVersion(currentVersion);
  const b = parseVersion(storeVersion);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i += 1) {
    const av = a[i] ?? 0;
    const bv = b[i] ?? 0;
    if (bv > av) return true;
    if (bv < av) return false;
  }
  return false;
}

const IOSUpdatePrompt: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [trackUrl, setTrackUrl] = useState<string>("");
  const [releaseNotes, setReleaseNotes] = useState<string>("");
  const [latestVersion, setLatestVersion] = useState<string>("");
  const isIosNative = useMemo(() => {
    try {
      const platform = Capacitor.getPlatform?.() || "web";
      return platform === "ios" && !!Capacitor.isNativePlatform?.();
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const checkUpdate = async () => {
      if (!isIosNative) return;
      try {
        const currentVersion = IOS_LATEST_VERSION;
        const res = await fetch(`${CROQEE_SERVER_URL}/api/app/version`);
        const data = (await res.json()) as VersionInfoResponse;
        const serverVersion = data?.ios?.latestVersion || "";
        setLatestVersion(serverVersion);
        const downloadUrl = data?.ios?.downloadUrl || "";

        if (serverVersion && downloadUrl && isStoreNewer(serverVersion, currentVersion) && isMounted) {
          setTrackUrl(downloadUrl);
          setReleaseNotes("");
          setOpen(true);
        }
      } catch (e) {
        console.log("App update check failed:", e);
      }
    };

    // Run shortly after app startup
    const timer = setTimeout(checkUpdate, 1500);
    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [isIosNative]);

  const handleUpdate = async () => {
    try {
      if (trackUrl) {
        await Browser.open({ url: trackUrl });
      }
    } catch {
      if (trackUrl) {
        window.open(trackUrl, "_blank");
      }
    } finally {
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} sx={{ "& .MuiPaper-root": { borderRadius: 0 } }}>
      <DialogTitle>New update available</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 1 }}>
          A newer version {latestVersion} is available on the App Store. Update now to get the latest improvements and bug fixes.
        </Typography>
        {releaseNotes && (
          <Typography variant="caption" sx={{ mt: 2, display: "block", whiteSpace: "pre-wrap" }}>
            {releaseNotes}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} sx={{ color: colors.brandColor, fontWeight: 600 }}>Remind me later</Button>
        <Button onClick={handleUpdate} autoFocus sx={{ color: colors.brandColor, fontWeight: 600 }}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IOSUpdatePrompt;



